import React, { useEffect, useRef, useState } from 'react'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import GoldQuoteIcon from 'src/images/svgs/icons/GoldQuoteIcon.svg'
import CustomLink from 'src/utils/customLink'
import { generateRandomId } from 'src/utils/domHelper'
import { PrismicLinkType } from 'src/typings/generated/graphql'
import OptionSelectModal, {
  OptionSelectModalContent,
} from 'src/components/organisms/ctas/optionSelectModal'
import * as Styles from './communitySuccessStories.module.scss'

export enum CommunitySuccessStoriesPrimaryButtonVariant {
  link = 'link',
  modal = 'modal',
}

export type CommunitySuccessStoryCard = {
  image?: IGatsbyImageData
  imageAlt?: string
  quote?: string
  name?: string
  subtitle?: string
}

export type CommunitySuccessStoriesContent = {
  anchor?: string
  headline?: string
  cards: CommunitySuccessStoryCard[]
  ctaHeadline?: string
  primaryButtonText?: string
  primaryButtonVariant?: string
  primaryButtonLink?: PrismicLinkType
  secondaryButtonText?: string
  secondaryButtonLink?: PrismicLinkType
  modal?: OptionSelectModalContent
}

type Props = {
  content: CommunitySuccessStoriesContent
}

const CommunitySuccessStories = ({ content }: Props) => {
  const {
    anchor = generateRandomId(),
    headline = '',
    cards,
    ctaHeadline = '',
    primaryButtonText = '',
    primaryButtonVariant = CommunitySuccessStoriesPrimaryButtonVariant.link,
    primaryButtonLink,
    secondaryButtonText = '',
    secondaryButtonLink,
    modal,
  } = content

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const carouselRef = useRef<HTMLDivElement>(null)

  const opensModal =
    primaryButtonVariant === CommunitySuccessStoriesPrimaryButtonVariant.modal

  useEffect(() => {
    const el = carouselRef.current
    if (!el) return undefined

    const handleScroll = () => {
      const slideWidth = el.clientWidth
      if (slideWidth === 0) return
      const index = Math.round(el.scrollLeft / slideWidth)
      setActiveIndex(index)
    }

    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [])

  const goToSlide = (index: number) => {
    const el = carouselRef.current
    if (!el) return
    el.scrollTo({ left: el.clientWidth * index, behavior: 'smooth' })
  }

  if (!cards || cards.length === 0) {
    return null
  }

  const renderCard = (card: CommunitySuccessStoryCard, index: number) => {
    const key = `community-success-story-${index}-${card.name ?? ''}`
    return (
      <div key={key} className={Styles.card}>
        {card.image && (
          <div className={Styles.cardImage}>
            <GatsbyImage image={card.image} alt={card.imageAlt ?? ''} />
          </div>
        )}
        <div className={Styles.cardBody}>
          <GoldQuoteIcon className={Styles.quoteIcon} />
          {card.quote && (
            <blockquote className={Styles.quote}>{card.quote}</blockquote>
          )}
          <div className={Styles.attribution}>
            {card.name && <span className={Styles.name}>{card.name}</span>}
            {card.subtitle && (
              <span className={Styles.subtitle}>{card.subtitle}</span>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <section id={anchor} className={Styles.communitySuccessStories}>
      <div className={Styles.inner}>
        {headline && <h2 className={Styles.headline}>{headline}</h2>}

        <div className={Styles.grid}>{cards.map(renderCard)}</div>

        <div
          ref={carouselRef}
          className={Styles.carousel}
          role="group"
          aria-roledescription="carousel"
          aria-label={headline || 'Community success stories'}
        >
          {cards.map(renderCard)}
        </div>

        {cards.length > 1 && (
          <div className={Styles.dots} role="tablist" aria-label="Select slide">
            {cards.map((card, index) => {
              const dotKey = `community-success-story-dot-${index}-${
                card.name ?? ''
              }`
              return (
                <button
                  key={dotKey}
                  type="button"
                  role="tab"
                  aria-selected={index === activeIndex}
                  aria-label={`Go to slide ${index + 1}`}
                  className={`${Styles.dot} ${
                    index === activeIndex ? Styles.activeDot : ''
                  }`}
                  onClick={() => goToSlide(index)}
                />
              )
            })}
          </div>
        )}

        {(ctaHeadline || primaryButtonText || secondaryButtonText) && (
          <div className={Styles.ctaSection}>
            {ctaHeadline && <p className={Styles.ctaHeadline}>{ctaHeadline}</p>}
            <div className={Styles.buttons}>
              {primaryButtonText &&
                (opensModal ? (
                  <button
                    type="button"
                    className={Styles.primaryButton}
                    onClick={() => setIsModalOpen(true)}
                  >
                    {primaryButtonText}
                  </button>
                ) : (
                  <CustomLink
                    className={Styles.primaryButton}
                    link={primaryButtonLink}
                  >
                    {primaryButtonText}
                  </CustomLink>
                ))}
              {secondaryButtonText && (
                <CustomLink
                  className={Styles.secondaryButton}
                  link={secondaryButtonLink}
                >
                  {secondaryButtonText}
                </CustomLink>
              )}
            </div>
          </div>
        )}
      </div>

      {opensModal && modal && (
        <OptionSelectModal
          content={modal}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </section>
  )
}

export default CommunitySuccessStories
